import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const salesCsHandoverArticle: BlogArticle = {
  key: BlogArticleKey.SALES_CS_HANDOVER,
  slug: BlogSlugs.SALES_CS_HANDOVER,
  categoryId: BlogCategory.CUSTOMER_KNOWLEDGE,
  date: "2026-09-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Passation sales vers CS : ne plus perdre le contexte client",
      excerpt:
        "Au kickoff, le client réexplique pourquoi il a signé. Ce n'est pas un problème d'onboarding, c'est une passation qui a laissé le contexte de la vente derrière elle.",
      description:
        "Comment passer un client du commercial au customer success sans perdre le contexte : ce qui se perd, un modèle de passation, puis ce qu'il faut suivre.",
      ctaTitle: "Une passation qui part des conversations",
      ctaText:
        "On rassemble ce que le client a dit pendant la vente, ses raisons d'acheter, ses critères, les engagements pris, pour que la personne qui reprend le compte parte du même point. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le contrat est signé. Deux semaines plus tard, le premier appel d'onboarding commence, et la personne côté client entend la question qu'elle redoutait : « Pour commencer, pouvez-vous nous rappeler votre contexte et ce que vous attendez de l'outil ? »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Elle a déjà tout raconté. Au commercial, en découverte, puis en démo, puis pendant la négociation. Elle a expliqué pourquoi l'outil actuel ne suffisait plus, qui devait valider, ce qui devait fonctionner avant la fin du trimestre. Et elle doit tout recommencer, devant quelqu'un qu'elle ne connaît pas.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce moment coûte plus cher qu'il n'y paraît. C'est la première impression de la relation après la signature, et elle dit au client que votre entreprise ne se parle pas.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Pourquoi le client doit se répéter au kickoff",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La cause n'est presque jamais la mauvaise volonté. Le commercial a passé des semaines avec le client, il sait beaucoup de choses, mais ce savoir est dans sa tête et dans des enregistrements que personne ne rouvre. La fiche CRM, elle, contient un montant, une date de signature et trois lignes de notes écrites entre deux rendez-vous.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quand le compte change de mains, c'est cette fiche qui passe, pas la mémoire du commercial. Le customer success manager, l'account manager ou la personne chargée de l'onboarding repart donc de zéro, et la seule source fiable qui lui reste, c'est le client lui-même.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Une passation ratée ne se voit pas le jour de la signature. Elle se voit au kickoff, quand le client comprend qu'il va devoir tout répéter.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce qui se perd entre la vente et l'onboarding",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Six éléments disparaissent presque toujours dans la passation. Ce sont aussi ceux dont l'équipe qui reprend le compte a le plus besoin.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**La raison d'achat.** Pas le besoin générique, la raison précise qui a fait basculer la décision. « On perd deux jours par mois à consolider les chiffres à la main » n'appelle pas le même onboarding que « notre direction veut un reporting unique ».",
            "**Les critères de succès énoncés.** Ce que le client a dit qu'il regarderait pour juger que l'achat était le bon. S'il a parlé d'un délai, d'un indicateur ou d'un usage précis, c'est à cela qu'il mesurera votre travail.",
            "**Les promesses du commercial.** Une intégration « prévue prochainement », un accompagnement renforcé, une fonctionnalité montrée en démo comme disponible. Le client s'en souvient mot pour mot, l'équipe qui livre ne les a jamais entendues.",
            "**Les risques et objections soulevés.** L'objection traitée pendant la vente n'a pas disparu, elle attend la première occasion de revenir. Une réserve sur l'adoption par les équipes terrain réapparaîtra dès les premières semaines.",
            "**Les parties prenantes.** Qui a signé, qui utilise, qui a freiné, qui doit être rassuré. Le sponsor n'est pas toujours l'utilisateur, et l'opposant du comité ne s'est pas volatilisé.",
            "**Les contraintes de calendrier.** Une date de lancement interne, un salon, un audit, une fin d'exercice. Le client a donné cette date pendant la vente, et l'onboarding est planifié sans la connaître.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Un modèle de passation qui tient en une page",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une bonne passation n'est pas un long document. C'est une page de champs précis, chacun rempli avec des faits vérifiables plutôt qu'avec des impressions. Le tableau ci-dessous donne, pour chaque champ, la différence entre une entrée vague et une entrée utile. Les exemples sont illustratifs.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Champ et entrée vague", "Entrée utile"],
          rows: [
            [
              "Raison d'achat : « Besoin de mieux piloter »",
              "« Le directeur financier refuse de continuer à consolider trois fichiers chaque fin de mois »",
            ],
            [
              "Critère de succès : « Satisfaction »",
              "« Premier reporting mensuel produit dans l'outil avant la clôture de décembre »",
            ],
            [
              "Promesses faites : « RAS »",
              "« Export vers leur outil comptable annoncé pour le deuxième trimestre, formation sur site offerte »",
            ],
            [
              "Risques : « Client un peu exigeant »",
              "« La responsable des opérations craint que ses équipes ne remplissent pas l'outil »",
            ],
            [
              "Parties prenantes : « Contact : Julie »",
              "« Julie utilise, le directeur financier sponsorise, la DSI doit valider l'accès aux données »",
            ],
            [
              "Calendrier : « Pas urgent »",
              "« Comité de direction le 15 janvier, les premiers chiffres doivent y être présentés »",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La colonne de gauche est ce qu'on écrit de mémoire, le soir de la signature. La colonne de droite est ce que le client a réellement dit, et c'est la seule qui permette à quelqu'un d'autre de reprendre la relation sans lui reposer les questions.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Qui fait quoi, et à quel moment",
        },
        {
          type: ArticleBlockType.H3,
          text: "Avant la signature",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La passation commence avant que le deal soit gagné. Dès que l'affaire entre en négociation, la personne qui reprendra le compte devrait pouvoir lire ce qui a été dit, et le commercial devrait savoir que ses promesses seront relues. Cette seule perspective rend les engagements plus prudents et plus précis.",
        },
        {
          type: ArticleBlockType.H3,
          text: "À la signature",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le commercial remplit la page de passation, puis la relit avec la personne qui reprend le compte, pendant une demi-heure. Le but n'est pas de résumer la vente, c'est de répondre aux questions de celle ou celui qui va livrer : qu'est-ce qui a été promis exactement, qui peut bloquer, qu'est-ce qui doit être visible au bout d'un mois.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Au kickoff",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le premier appel ne commence plus par une question ouverte, il commence par une reformulation. « Vous nous avez dit que l'enjeu était de présenter les premiers chiffres au comité du 15 janvier, et que l'adoption par les équipes terrain vous inquiétait. Est-ce toujours le cas ? » Le client corrige ce qui a bougé, et il sait qu'il a été entendu.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quand c'est possible, le commercial assiste à ce premier appel. Sa présence dit au client que la relation continue, et elle permet de lever tout de suite les malentendus sur ce qui avait été convenu.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Passation de mémoire ou passation depuis les conversations",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Passation de mémoire",
            items: [
              "Quelques notes écrites après la signature",
              "Les promesses dépendent de ce dont le commercial se souvient",
              "Les objections traitées sont oubliées",
              "Le client réexplique tout au kickoff",
            ],
          },
          good: {
            title: "Passation depuis les conversations",
            items: [
              "Les raisons d'achat citées avec les mots du client",
              "Chaque engagement noté avec son auteur et son échéance",
              "Les réserves exprimées pendant la vente restent visibles",
              "Le kickoff commence par une reformulation à valider",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La différence ne tient pas à la discipline du commercial. Même le plus rigoureux résume, et résumer, c'est choisir. Une passation construite à partir des échanges eux-mêmes laisse la personne qui reprend le compte revenir à la phrase exacte quand un doute apparaît.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Vérifier que ce qui a été promis correspond à ce qui est livré",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les écarts entre la vente et la livraison sont rarement des mensonges. Ce sont des formulations optimistes, des « c'est prévu » devenus « c'est disponible » dans la tête du client, des démos qui montraient un paramétrage que le client n'aura pas au départ.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le contrôle est simple à décrire : reprendre chaque engagement pris pendant la vente, le confronter au plan d'onboarding, et traiter les écarts avant le kickoff plutôt qu'après. Un écart annoncé par l'équipe est une précision. Le même écart découvert par le client est une promesse non tenue.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Listez chaque engagement pris par le commercial, avec la phrase telle qu'elle a été dite.",
            "Marquez ceux qui sont livrés dès le départ, ceux qui arriveront plus tard, et ceux qui ne le seront pas.",
            "Pour les deux dernières catégories, prévoyez la phrase à dire au client au kickoff.",
            "Remontez à l'équipe commerciale les promesses qui reviennent et que le produit ne tient pas encore.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce qu'il faut suivre après la passation",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La passation n'est pas un événement, c'est le début d'un suivi. Trois choses méritent d'être regardées chaque semaine pendant les premiers mois.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Les engagements et leurs échéances.** Ceux de votre équipe comme ceux du client. Un accès aux données promis par la DSI du client pour la fin du mois est aussi bloquant qu'une configuration promise par vous.",
            "**Les premiers signaux de risque.** Une remarque sur le temps de prise en main, un utilisateur clé qui ne vient plus aux points, une comparaison avec l'ancien outil. Isolés, ils ne disent rien ; relus ensemble, ils annoncent souvent un désengagement.",
            "**Les clients qui se taisent.** Un client actif dont on n'a plus de nouvelles depuis plusieurs semaines n'est pas un client satisfait par défaut. Le silence après un onboarding est plus souvent un abandon qu'une autonomie.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce suivi n'a de valeur que s'il se fait au même endroit que la passation. Si les engagements sont dans un document, les risques dans la tête du CSM et les dates dans un agenda, personne ne voit le client dans son ensemble.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "La fiche client comme page de passation",
          text: "Dans Meidly, chaque client a une fiche alimentée par ses échanges : une synthèse mise à jour après chaque appel, les engagements avec leur auteur et leur échéance, les deals détectés, et les passages étiquetés (raison d'achat, critère de décision, risque de churn). La vue d'ensemble liste les engagements en retard et les clients restés silencieux.",
          linkLabel: "Voir les fiches clients",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.H2,
          text: "Les limites d'une passation, même bien faite",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Aucun modèle ne remplace la conversation entre la personne qui a vendu et celle qui reprend le compte. Une synthèse générée reste un brouillon à relire : elle rassemble ce qui a été dit, elle ne sait pas ce qui a été compris à demi-mot ni ce que le commercial a senti sans l'entendre.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et une passation ne vaut que si les conversations ont été enregistrées, avec l'accord des participants. Ce qui a été dit dans un couloir ou au téléphone sans trace ne passera que par la mémoire, avec tout ce qu'elle oublie.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le critère final reste le kickoff. Si le client n'a rien à répéter et passe l'appel à corriger quelques détails plutôt qu'à tout réexpliquer, la passation a fonctionné.",
        },
      ],
      faq: [
        {
          question: "Qui doit rédiger la passation, le commercial ou le customer success ?",
          answer:
            "Le commercial, parce que c'est lui qui a entendu le client. Mais la personne qui reprend le compte doit la relire avec lui et poser ses questions avant le kickoff. Une passation écrite sans être relue reste un document que personne ne s'approprie.",
        },
        {
          question: "Quand faut-il commencer la passation ?",
          answer:
            "Avant la signature, dès que l'affaire entre en négociation. C'est à ce moment que les engagements se prennent, et savoir qu'ils seront relus par l'équipe qui livre les rend plus précis.",
        },
        {
          question: "Que mettre dans un modèle de passation sales vers CS ?",
          answer:
            "Six champs suffisent : la raison d'achat, les critères de succès énoncés par le client, les promesses faites pendant la vente, les risques et objections soulevés, les parties prenantes et leur rôle, et les contraintes de calendrier. Chacun doit être rempli avec ce que le client a dit, pas avec une impression.",
        },
        {
          question: "Comment éviter que le client se répète au kickoff ?",
          answer:
            "En ouvrant l'appel par une reformulation de ce qu'il a dit pendant la vente, puis en lui demandant ce qui a changé. Le client corrige quelques points au lieu de tout reprendre, et comprend que l'équipe s'est transmis son contexte.",
        },
        {
          question: "Que suivre dans les premiers mois après la passation ?",
          answer:
            "Les engagements des deux côtés avec leurs échéances, les premiers signaux de risque exprimés dans les échanges, et les clients qui ne donnent plus de nouvelles. Ces trois éléments annoncent la plupart des désengagements avant qu'ils deviennent visibles.",
        },
      ],
    },
    en: {
      title: "Sales to CS handover: stop losing the customer context",
      excerpt:
        "At the kickoff, the customer explains again why they signed. That is not an onboarding problem, it is a handover that left the context of the sale behind.",
      description:
        "How to hand a customer from sales to customer success without losing context: what gets lost, a handover template, and what to track afterwards.",
      ctaTitle: "A handover built from the conversations",
      ctaText:
        "We gather what the customer said during the sale, their reasons to buy, their criteria, the commitments made, so the person taking over the account starts from the same place. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The contract is signed. Two weeks later the first onboarding call starts, and the customer hears the question they were dreading: "To start, could you remind us of your context and what you expect from the tool?"',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "They have already told the whole story. To the seller, in discovery, then in the demo, then during the negotiation. They explained why the current tool was no longer enough, who had to approve, what had to work before the end of the quarter. And now they have to start over, in front of someone they do not know.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That moment costs more than it seems. It is the first impression of the relationship after signature, and it tells the customer that your company does not talk to itself.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Why the customer has to repeat themselves at kickoff",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The cause is almost never bad will. The seller spent weeks with the customer and knows a lot, but that knowledge sits in their head and in recordings nobody reopens. The CRM record holds an amount, a close date and three lines of notes written between two meetings.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "When the account changes hands, the record is what moves, not the seller's memory. The customer success manager, the account manager or whoever runs onboarding starts from scratch, and the only reliable source left is the customer.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A failed handover does not show on the day of signature. It shows at kickoff, when the customer realises they will have to repeat everything.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What gets lost between the sale and onboarding",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Six things nearly always disappear in the handover. They are also the ones the team taking over the account needs most.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            '**The reason they bought.** Not the generic need, the precise reason that tipped the decision. "We lose two days a month consolidating the numbers by hand" does not call for the same onboarding as "our leadership wants a single report".',
            "**The success criteria they stated.** What the customer said they would look at to judge the purchase. If they mentioned a deadline, a metric or a specific use, that is what they will measure your work against.",
            '**The seller\'s promises.** An integration "coming soon", extra support, a feature shown in the demo as available. The customer remembers them word for word, the delivery team never heard them.',
            "**The risks and objections raised.** An objection handled during the sale has not gone away, it is waiting for its first chance to return. A doubt about adoption by field teams will resurface in the first weeks.",
            "**The stakeholders.** Who signed, who uses it, who pushed back, who needs reassurance. The sponsor is not always the user, and the sceptic on the committee has not vanished.",
            "**The timing constraints.** An internal launch date, a trade show, an audit, a fiscal year end. The customer gave that date during the sale, and onboarding is planned without it.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "A handover template that fits on one page",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A good handover is not a long document. It is one page of precise fields, each filled with verifiable facts rather than impressions. The table below shows, for each field, the difference between a vague entry and a useful one. The examples are illustrative.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Field and vague entry", "Useful entry"],
          rows: [
            [
              'Reason to buy: "Needs better visibility"',
              '"The CFO refuses to keep consolidating three spreadsheets at every month end"',
            ],
            [
              'Success criterion: "Satisfaction"',
              '"First monthly report produced in the tool before the December close"',
            ],
            [
              'Promises made: "Nothing special"',
              '"Export to their accounting tool announced for Q2, on site training offered"',
            ],
            [
              'Risks: "Somewhat demanding customer"',
              '"The head of operations fears her teams will not fill in the tool"',
            ],
            [
              'Stakeholders: "Contact: Julie"',
              '"Julie uses it, the CFO sponsors it, IT must approve access to the data"',
            ],
            [
              'Timing: "Not urgent"',
              '"Executive committee on 15 January, the first numbers must be presented there"',
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The left column is what gets written from memory on the evening of signature. The right column is what the customer actually said, and it is the only one that lets someone else take over the relationship without asking the questions again.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Who does what, and when",
        },
        {
          type: ArticleBlockType.H3,
          text: "Before signature",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The handover starts before the deal is won. As soon as it enters negotiation, the person who will take over the account should be able to read what was said, and the seller should know their promises will be read. That alone makes commitments more careful and more precise.",
        },
        {
          type: ArticleBlockType.H3,
          text: "At signature",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The seller fills in the handover page, then walks through it with the person taking over, for half an hour. The goal is not to summarise the sale, it is to answer the questions of whoever will deliver: what exactly was promised, who can block, what must be visible after a month.",
        },
        {
          type: ArticleBlockType.H3,
          text: "At kickoff",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The first call no longer opens with an open question, it opens with a restatement. "You told us the goal was to present the first numbers at the 15 January committee, and that adoption by the field teams worried you. Is that still the case?" The customer corrects what has moved, and knows they were heard.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "When possible, the seller joins that first call. Their presence tells the customer the relationship continues, and it clears up any misunderstanding about what was agreed on the spot.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Handover from memory or handover from the conversations",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Handover from memory",
            items: [
              "A few notes written after signature",
              "Promises depend on what the seller remembers",
              "Handled objections are forgotten",
              "The customer explains everything again at kickoff",
            ],
          },
          good: {
            title: "Handover from the conversations",
            items: [
              "Reasons to buy quoted in the customer's own words",
              "Each commitment logged with its owner and due date",
              "Doubts raised during the sale stay visible",
              "Kickoff opens with a restatement to confirm",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difference is not the seller's discipline. Even the most rigorous one summarises, and summarising means choosing. A handover built from the exchanges themselves lets the person taking over go back to the exact sentence whenever a doubt comes up.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Checking that what was promised matches what is delivered",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Gaps between sale and delivery are rarely lies. They are optimistic wordings, a "it is planned" that became "it is available" in the customer\'s mind, demos showing a setup the customer will not have at first.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The check is simple to describe: take every commitment made during the sale, set it against the onboarding plan, and deal with the gaps before kickoff rather than after. A gap announced by your team is a clarification. The same gap discovered by the customer is a broken promise.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "List every commitment the seller made, with the sentence as it was said.",
            "Mark which are delivered from day one, which will come later, and which will not be delivered.",
            "For the last two groups, prepare the sentence to say to the customer at kickoff.",
            "Send back to sales the promises that keep coming up and that the product does not yet keep.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "What to track after the handover",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The handover is not an event, it is the start of follow-up. Three things deserve a weekly look during the first months.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Commitments and their due dates.** Your team's and the customer's. Data access promised by the customer's IT team for month end blocks as much as a configuration promised by you.",
            "**The first risk signals.** A remark about how long it takes to get started, a key user who stops attending check-ins, a comparison with the old tool. On their own they say nothing; read together they often announce disengagement.",
            "**Customers going quiet.** An active customer you have not heard from in several weeks is not a satisfied customer by default. Silence after onboarding is more often abandonment than autonomy.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This follow-up only has value if it happens in the same place as the handover. If commitments live in a document, risks in the CSM's head and dates in a calendar, nobody sees the customer as a whole.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "The customer file as a handover page",
          text: "In Meidly, each customer has a file fed by its exchanges: a synthesis updated after every call, commitments with their owner and due date, detected deals, and tagged passages (buying reason, decision criteria, churn risk). The overview lists overdue commitments and customers gone quiet.",
          linkLabel: "See customer files",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.H2,
          text: "The limits of a handover, even a good one",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "No template replaces the conversation between the person who sold and the person taking over. A generated synthesis remains a draft to review: it gathers what was said, it does not know what was half implied or what the seller sensed without hearing it.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And a handover is only as good as the conversations that were recorded, with the participants' consent. What was said in a corridor or on an unrecorded phone call will only travel through memory, with everything memory forgets.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The final test is still the kickoff. If the customer has nothing to repeat and spends the call correcting a few details rather than explaining everything again, the handover worked.",
        },
      ],
      faq: [
        {
          question: "Who should write the handover, sales or customer success?",
          answer:
            "The seller, because they heard the customer. But the person taking over the account should review it with them and ask their questions before kickoff. A handover written and never reviewed stays a document nobody owns.",
        },
        {
          question: "When should the handover start?",
          answer:
            "Before signature, as soon as the deal enters negotiation. That is when commitments are made, and knowing the delivery team will read them makes them more precise.",
        },
        {
          question: "What goes into a sales to CS handover template?",
          answer:
            "Six fields are enough: the reason to buy, the success criteria the customer stated, the promises made during the sale, the risks and objections raised, the stakeholders and their roles, and the timing constraints. Each should be filled with what the customer said, not with an impression.",
        },
        {
          question: "How do you stop the customer repeating themselves at kickoff?",
          answer:
            "Open the call by restating what they said during the sale, then ask what has changed. The customer corrects a few points instead of starting over, and understands that the team passed their context along.",
        },
        {
          question: "What should you track in the first months after the handover?",
          answer:
            "Commitments on both sides with their due dates, the first risk signals voiced in the exchanges, and customers who stop getting in touch. These three announce most disengagements before they become visible.",
        },
      ],
    },
  },
};
